import React from "react";
import { Button, Empty, List, message, Popconfirm, Tag } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useGetAlerts } from "~/services/alert/alert.queries";
import { alertServices } from "~/services/alert/alert.service";
import { TAlert } from "~/types/alerts";
import { capitalize } from "~/utils";

export const Alerts: React.FC = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useGetAlerts({ page: 1, pageSize: 5, status: "open" });

  const dismissMutation = useMutation({
    mutationFn: (alertId: string) => alertServices.dismissAlert(alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alert"] });
      message.success("Alert dismissed");
    },
    onError: () => {
      message.error("Failed to dismiss alert");
    },
  });

  const getTagColor = (type: string) => {
    switch (type) {
      case "DUE_IN_1_DAY":
      case "OVERDUE":
        return "red";
      case "DUE_IN_2_DAYS":
        return "orange";
      case "DUE_IN_3_DAYS":
        return "yellow";
      default:
        return "blue";
    }
  };

  return (
    <div className="pb-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-medium">Alerts</h3>
        <Link to="/alerts">
          <Button type="link" size="small">
            View All
          </Button>
        </Link>
      </div>
      {isLoading ? (
        <div>Loading...</div>
      ) : !data || data.items.length === 0 ? (
        <div className="flex justify-center items-center h-40 mb-4">
          <Empty
            description={
              <span className="text-gray-500 text-base font-medium">
                No open alerts
              </span>
            }
          />
        </div>
      ) : (
        <List
          dataSource={data.items}
          renderItem={(alert: TAlert) => (
            <List.Item
              actions={[
                <Popconfirm
                  title="Dismiss alert?"
                  onConfirm={() => dismissMutation.mutate(alert.id)}
                  okText="Dismiss"
                  cancelText="Cancel"
                  key="dismiss"
                >
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    loading={dismissMutation.isPending}
                  />
                </Popconfirm>,
              ]}
            >
              <div className="flex-1">
                <Tag color={getTagColor(alert.type)}>
                  {capitalize(alert.type)}
                </Tag>
                <span>{alert.message}</span>
              </div>
            </List.Item>
          )}
        />
      )}
    </div>
  );
};
